<template>
  <transition name="fade">
    <div v-if="isVisible" class="modal-overlay" @click="onClose">
      <div class="modal-content submissions-modal" @click.stop>
        <div class="modal-header">
          <h3>
            {{ t('competitions.submissionsFor') || 'Submissions for' }}:
            {{ competition && competition.name }}
          </h3>
          <button class="close-btn" @click="onClose">&times;</button>
        </div>

        <transition name="fade">
          <div
            v-if="showNotificationFlag"
            class="notification"
            :class="notificationType"
          >
            {{ notificationMessage }}
          </div>
        </transition>

        <div class="modal-body">
          <div class="toolbar">
            <input
              type="text"
              v-model="searchQuery"
              class="search-input"
              :placeholder="t('general.search') || 'Search'"
            />

            <select v-model="sourceFilter" class="filter-select">
              <option value="all">{{ t('general.all') || 'All' }}</option>
              <option value="race">
                {{ t('competitions.competition') || 'Competition' }}
              </option>
              <option value="cup">{{ t('cups.cups') || 'Cup' }}</option>
            </select>

            <button class="btn btn-secondary" @click="toggleAllDetails">
              {{
                allExpanded
                  ? t('general.collapseAll') || 'Collapse All'
                  : t('general.expandAll') || 'Expand All'
              }}
            </button>

            <button
              class="btn btn-danger"
              :disabled="selectedCount === 0 || loading"
              @click="deleteSelected"
            >
              {{ t('general.deleteSelected') || 'Delete Selected' }} ({{
                selectedCount
              }})
            </button>

            <button class="btn btn-secondary" @click="exportCsv">
              {{ t('general.exportCsv') || 'Export CSV' }}
            </button>

            <details ref="columnSelectorRef" class="column-selector">
              <summary class="column-selector-summary">
                {{ columnsLabel }} ({{ visibleFieldKeys.length }})
              </summary>

              <div class="column-selector-menu">
                <div v-if="availableFieldKeys.length === 0" class="column-empty">
                  {{ noSubmissionFieldsText }}
                </div>

                <label
                  v-for="fieldKey in availableFieldKeys"
                  :key="fieldKey"
                  class="column-option"
                  :title="fieldKey"
                >
                  <input
                    type="checkbox"
                    :checked="isColumnVisible(fieldKey)"
                    @change="toggleColumnVisibility(fieldKey, $event)"
                  />
                  <span class="column-option-label">{{
                    getColumnLabel(fieldKey)
                  }}</span>
                </label>
              </div>
            </details>
          </div>

          <div v-if="loading" class="loading">
            {{ t('general.loading') || 'Loading...' }}
          </div>

          <div v-else class="table-wrap">
            <table class="submissions-table">
              <thead>
                <tr>
                  <th class="checkbox-header">
                    <input
                      type="checkbox"
                      :checked="allSelected"
                      @change="toggleSelectAll($event)"
                    />
                  </th>
                  <th class="source-header">{{ sourceHeaderLabel }}</th>
                  <th
                    v-for="fieldKey in visibleFieldKeys"
                    :key="fieldKey"
                    class="dynamic-column-header"
                    :title="fieldKey"
                  >
                    {{ getColumnLabel(fieldKey) }}
                  </th>
                  <th class="date-cell-header">{{ submittedAtHeaderLabel }}</th>
                  <th>{{ t('general.actions') || 'Actions' }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredSubmissions.length === 0">
                  <td :colspan="visibleColumnSpan" class="empty-state">
                    {{ noSubmissionsFoundText }}
                  </td>
                </tr>
                <template v-for="s in filteredSubmissions" :key="s.id">
                  <tr>
                    <td class="checkbox-cell">
                      <input
                        type="checkbox"
                        :checked="isSelected(s.id)"
                        @change="toggleSelect(s.id, $event)"
                      />
                    </td>
                    <td class="source-cell">
                      <span :class="['source-pill', getSubmissionSource(s)]">
                        {{ getSubmissionSourceLabel(s) }}
                      </span>
                    </td>
                    <td
                      v-for="fieldKey in visibleFieldKeys"
                      :key="`${s.id}-${fieldKey}`"
                      class="dynamic-cell"
                    >
                      <span
                        class="dynamic-cell-value"
                        :title="getColumnValue(s, fieldKey)"
                      >
                        {{ getColumnValue(s, fieldKey) }}
                      </span>
                    </td>
                    <td class="date-cell">
                      {{ formatDateTime(s.submitted_at) }}
                    </td>
                    <td class="actions-cell">
                      <div class="actions">
                        <button
                          class="btn btn-secondary btn-sm"
                          @click="toggleDetails(s)"
                        >
                          {{
                            t('competitions.submissionDetails') ||
                            'Submission Details'
                          }}
                        </button>
                        <button
                          class="btn btn-secondary btn-sm"
                          @click="startEditing(s)"
                        >
                          {{
                            t('competitions.editSubmission') || 'Edit Submission'
                          }}
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="isExpanded(s.id)">
                    <td :colspan="visibleColumnSpan" class="details-cell">
                      <div class="details">
                        <table class="kv-table">
                          <thead>
                            <tr>
                              <th>{{ t('general.field') || 'Field' }}</th>
                              <th>{{ t('general.value') || 'Value' }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="(row, idx) in getAthleteFieldEntries(s)"
                              :key="idx"
                            >
                              <td class="kv-key">{{ row[0] }}</td>
                              <td class="kv-value">{{ row[1] }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="onClose">
            {{ t('general.close') || 'Close' }}
          </button>
        </div>

        <transition name="fade">
          <div
            v-if="isEditModalVisible"
            class="edit-modal-overlay"
            @click="cancelEditing"
          >
            <div class="modal-content submission-edit-modal" @click.stop>
              <div class="submission-edit-header">
                <div class="submission-edit-title-group">
                  <h4>
                    {{ t('competitions.editSubmission') || 'Edit Submission' }}
                  </h4>
                  <p v-if="editingSubmission" class="submission-edit-subtitle">
                    #{{ editingSubmission.id }}
                  </p>
                </div>
                <button class="close-btn" @click="cancelEditing">&times;</button>
              </div>

              <div class="submission-edit-body">
                <div v-if="!hasEditableFields" class="edit-empty">
                  {{
                    t('competitions.noEditableFields') || 'No editable fields'
                  }}
                </div>

                <form
                  v-else
                  class="edit-form"
                  @submit.prevent="saveSubmissionEdit()"
                >
                  <section
                    v-if="editableFormFields.length > 0"
                    class="edit-section"
                  >
                    <h5 class="edit-section-title">
                      {{ t('competitions.submission') || 'Submission' }}
                    </h5>
                    <div class="edit-grid">
                      <template
                        v-for="field in editableFormFields"
                        :key="`field-${field.name}`"
                      >
                        <div
                          v-if="field.type === 'name'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <div class="edit-name-grid">
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.firstNameLabel || 'First Name'
                              }}</label>
                              <input
                                v-model="editDraft.fields[field.name].first"
                                type="text"
                                class="edit-input"
                              />
                            </div>
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.lastNameLabel || 'Last Name'
                              }}</label>
                              <input
                                v-model="editDraft.fields[field.name].last"
                                type="text"
                                class="edit-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          v-else-if="field.type === 'textarea'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <textarea
                            v-model="editDraft.fields[field.name]"
                            rows="4"
                            class="edit-textarea"
                          ></textarea>
                        </div>

                        <div
                          v-else-if="field.type === 'date'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <div class="edit-date-grid">
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.dayLabel || 'Day'
                              }}</label>
                              <input
                                v-model="editDraft.fields[field.name].day"
                                type="text"
                                class="edit-input"
                                :placeholder="field.dayPlaceholder || 'Day'"
                              />
                            </div>
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.monthLabel || 'Month'
                              }}</label>
                              <input
                                v-model="editDraft.fields[field.name].month"
                                type="text"
                                class="edit-input"
                                :placeholder="field.monthPlaceholder || 'Month'"
                              />
                            </div>
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.yearLabel || 'Year'
                              }}</label>
                              <input
                                v-model="editDraft.fields[field.name].year"
                                type="text"
                                class="edit-input"
                                :placeholder="field.yearPlaceholder || 'Year'"
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          v-else-if="['select', 'radio'].includes(field.type)"
                          class="edit-group"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <select
                            v-model="editDraft.fields[field.name]"
                            class="edit-input"
                          >
                            <option value="">
                              {{
                                field.selectEmptyOption ||
                                t('registration.selectOption') ||
                                'Please select...'
                              }}
                            </option>
                            <option
                              v-for="option in getFieldOptions(field)"
                              :key="`option-${field.name}-${option}`"
                              :value="option"
                            >
                              {{ option }}
                            </option>
                          </select>
                        </div>

                        <div
                          v-else-if="field.type === 'checkbox'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <div
                            v-if="getFieldOptions(field).length > 0"
                            class="edit-checkbox-grid"
                          >
                            <label
                              v-for="option in getFieldOptions(field)"
                              :key="`checkbox-${field.name}-${option}`"
                              class="edit-checkbox-option"
                            >
                              <input
                                type="checkbox"
                                :checked="
                                  isCheckboxDraftSelected(
                                    editDraft.fields[field.name],
                                    option
                                  )
                                "
                                @change="
                                  toggleCheckboxDraftValue(
                                    'fields',
                                    field.name,
                                    option,
                                    $event
                                  )
                                "
                              />
                              <span>{{ option }}</span>
                            </label>
                          </div>
                          <label
                            v-else
                            class="edit-checkbox-option edit-checkbox-single"
                          >
                            <input
                              type="checkbox"
                              :checked="!!editDraft.fields[field.name]"
                              @change="
                                editDraft.fields[field.name] =
                                  $event.target.checked
                              "
                            />
                            <span>{{
                              field.label || getColumnLabel(field.name)
                            }}</span>
                          </label>
                        </div>

                        <div v-else class="edit-group">
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <input
                            v-model="editDraft.fields[field.name]"
                            :type="getEditInputType(field)"
                            class="edit-input"
                          />
                        </div>
                      </template>
                    </div>
                  </section>

                  <section
                    v-if="editableTeamManagerFields.length > 0"
                    class="edit-section"
                  >
                    <h5 class="edit-section-title">
                      {{ t('competitions.teamManager') || 'Team Manager' }}
                    </h5>
                    <div class="edit-grid">
                      <template
                        v-for="field in editableTeamManagerFields"
                        :key="`team-manager-${field.name}`"
                      >
                        <div
                          v-if="field.type === 'name'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <div class="edit-name-grid">
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.firstNameLabel || 'First Name'
                              }}</label>
                              <input
                                v-model="editDraft.teamManager[field.name].first"
                                type="text"
                                class="edit-input"
                              />
                            </div>
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.lastNameLabel || 'Last Name'
                              }}</label>
                              <input
                                v-model="editDraft.teamManager[field.name].last"
                                type="text"
                                class="edit-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          v-else-if="field.type === 'textarea'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <textarea
                            v-model="editDraft.teamManager[field.name]"
                            rows="4"
                            class="edit-textarea"
                          ></textarea>
                        </div>

                        <div
                          v-else-if="field.type === 'date'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <div class="edit-date-grid">
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.dayLabel || 'Day'
                              }}</label>
                              <input
                                v-model="editDraft.teamManager[field.name].day"
                                type="text"
                                class="edit-input"
                                :placeholder="field.dayPlaceholder || 'Day'"
                              />
                            </div>
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.monthLabel || 'Month'
                              }}</label>
                              <input
                                v-model="editDraft.teamManager[field.name].month"
                                type="text"
                                class="edit-input"
                                :placeholder="field.monthPlaceholder || 'Month'"
                              />
                            </div>
                            <div class="edit-input-group">
                              <label class="edit-sublabel">{{
                                field.yearLabel || 'Year'
                              }}</label>
                              <input
                                v-model="editDraft.teamManager[field.name].year"
                                type="text"
                                class="edit-input"
                                :placeholder="field.yearPlaceholder || 'Year'"
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          v-else-if="['select', 'radio'].includes(field.type)"
                          class="edit-group"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <select
                            v-model="editDraft.teamManager[field.name]"
                            class="edit-input"
                          >
                            <option value="">
                              {{
                                field.selectEmptyOption ||
                                t('registration.selectOption') ||
                                'Please select...'
                              }}
                            </option>
                            <option
                              v-for="option in getFieldOptions(field)"
                              :key="`team-option-${field.name}-${option}`"
                              :value="option"
                            >
                              {{ option }}
                            </option>
                          </select>
                        </div>

                        <div
                          v-else-if="field.type === 'checkbox'"
                          class="edit-group edit-group-span"
                        >
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <div
                            v-if="getFieldOptions(field).length > 0"
                            class="edit-checkbox-grid"
                          >
                            <label
                              v-for="option in getFieldOptions(field)"
                              :key="`team-checkbox-${field.name}-${option}`"
                              class="edit-checkbox-option"
                            >
                              <input
                                type="checkbox"
                                :checked="
                                  isCheckboxDraftSelected(
                                    editDraft.teamManager[field.name],
                                    option
                                  )
                                "
                                @change="
                                  toggleCheckboxDraftValue(
                                    'teamManager',
                                    field.name,
                                    option,
                                    $event
                                  )
                                "
                              />
                              <span>{{ option }}</span>
                            </label>
                          </div>
                          <label
                            v-else
                            class="edit-checkbox-option edit-checkbox-single"
                          >
                            <input
                              type="checkbox"
                              :checked="!!editDraft.teamManager[field.name]"
                              @change="
                                editDraft.teamManager[field.name] =
                                  $event.target.checked
                              "
                            />
                            <span>{{
                              field.label || getColumnLabel(field.name)
                            }}</span>
                          </label>
                        </div>

                        <div v-else class="edit-group">
                          <label class="edit-label">{{
                            field.label || getColumnLabel(field.name)
                          }}</label>
                          <input
                            v-model="editDraft.teamManager[field.name]"
                            :type="getEditInputType(field)"
                            class="edit-input"
                          />
                        </div>
                      </template>
                    </div>
                  </section>
                </form>
              </div>

              <div class="submission-edit-footer">
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="savingEdit"
                  @click="cancelEditing"
                >
                  {{ t('competitions.cancelEdit') || 'Cancel Edit' }}
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="savingEdit || !hasEditableFields"
                  @click="saveSubmissionEdit()"
                >
                  {{
                    savingEdit
                      ? t('competitions.savingSubmission') || 'Saving...'
                      : t('competitions.saveSubmissionChanges') ||
                        'Save Changes'
                  }}
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { useNotification } from '@/composables/useNotification'

export default {
  name: 'SubmissionsList',
  props: {
    isVisible: { type: Boolean, required: true },
    competition: { type: Object, required: false },
  },
  emits: ['close', 'deleted-count'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const {
      showNotificationFlag,
      notificationMessage,
      notificationType,
      showNotification,
    } = useNotification()
    const loading = ref(false)
    const submissions = ref([])
    const registrationForm = ref(null)
    const searchQuery = ref('')
    const sourceFilter = ref('all') // all | race | cup
    const expandedIds = ref(new Set())
    const selectedIds = ref(new Set())
    const visibleFieldKeys = ref([])
    const columnSelectorRef = ref(null)
    const editingSubmissionId = ref(null)
    const editingSubmission = ref(null)
    const editDraft = ref({ fields: {}, teamManager: {} })
    const savingEdit = ref(false)

    const translateOrFallback = (key, fallback) => {
      const translated = t(key)
      return translated && translated !== key ? translated : fallback
    }

    const columnsLabel = computed(() =>
      translateOrFallback('general.columns', 'Columns')
    )
    const sourceHeaderLabel = computed(() =>
      translateOrFallback('general.source', 'Source')
    )
    const submittedAtHeaderLabel = computed(() =>
      translateOrFallback('competitions.submittedAt', 'Submitted At')
    )
    const noSubmissionFieldsText = computed(() =>
      translateOrFallback(
        'competitions.noSubmissionFields',
        'No submission fields found'
      )
    )
    const noSubmissionsFoundText = computed(() =>
      translateOrFallback('competitions.noSubmissionsFound', 'No submissions found')
    )

    const resetEditState = () => {
      editingSubmissionId.value = null
      editingSubmission.value = null
      editDraft.value = { fields: {}, teamManager: {} }
      savingEdit.value = false
    }

    const fetchSubmissions = async () => {
      if (!props.competition || !props.competition.id) return
      loading.value = true
      try {
        const res = await axios.get(
          `/registration/competition/${props.competition.id}/submissions`
        )
        submissions.value = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        console.error('Error fetching submissions:', e)
        submissions.value = []
        showNotification(
          t('competitions.failedToLoadSubmissions') ||
            'Failed to load submissions',
          'error'
        )
      } finally {
        loading.value = false
      }
    }

    const fetchRegistrationForm = async (formId) => {
      if (!formId) {
        registrationForm.value = null
        return
      }

      try {
        const res = await axios.get(`/registration/form/${formId}`)
        registrationForm.value = res.data || null
      } catch (e) {
        console.error('Error fetching registration form:', e)
        registrationForm.value = null
      }
    }

    watch(
      () => [props.isVisible, props.competition && props.competition.id],
      async ([visible]) => {
        if (visible) {
          resetEditState()
          visibleFieldKeys.value = []
          await fetchRegistrationForm(props.competition?.registration_form_id)
          await fetchSubmissions()
        } else {
          resetEditState()
        }
      }
    )

    watch(
      () => props.competition?.registration_form_id,
      async (formId) => {
        if (!props.isVisible) return
        await fetchRegistrationForm(formId)
      }
    )

    const closeColumnSelector = () => {
      if (columnSelectorRef.value?.open) {
        columnSelectorRef.value.open = false
      }
    }

    const handleDocumentPointerDown = (event) => {
      if (!columnSelectorRef.value) return
      if (columnSelectorRef.value.contains(event.target)) return
      closeColumnSelector()
    }

    onMounted(() => {
      document.addEventListener('pointerdown', handleDocumentPointerDown, true)
    })

    onBeforeUnmount(() => {
      document.removeEventListener(
        'pointerdown',
        handleDocumentPointerDown,
        true
      )
    })

    const flattenedSubmissionDataById = computed(() => {
      const flattenedMap = new Map()

      for (const submission of submissions.value) {
        flattenedMap.set(
          submission.id,
          flattenObject(parseSubmissionData(submission.submission_data))
        )
      }

      return flattenedMap
    })

    const getFlattenedSubmissionData = (submission) =>
      flattenedSubmissionDataById.value.get(submission.id) || {}

    const collectFieldKeysFromSubmissions = (submissionList) => {
      const discoveredKeys = []
      const seen = new Set()
      const orderedSubmissions = [...submissionList].sort(
        (a, b) => Number(a.id) - Number(b.id)
      )

      for (const submission of orderedSubmissions) {
        const flattened = getFlattenedSubmissionData(submission)
        for (const fieldKey of Object.keys(flattened)) {
          if (!seen.has(fieldKey)) {
            seen.add(fieldKey)
            discoveredKeys.push(fieldKey)
          }
        }
      }

      return discoveredKeys
    }

    const getOrderedDynamicFieldKeys = (submissionList) => {
      const discoveredKeys = collectFieldKeysFromSubmissions(submissionList)
      const discoveredKeySet = new Set(discoveredKeys)
      const orderedKeys = []
      const formFields = Array.isArray(registrationForm.value?.fields)
        ? registrationForm.value.fields
        : []
      const teamManagerFields = Array.isArray(
        registrationForm.value?.settings?.teamManagerFields
      )
        ? registrationForm.value.settings.teamManagerFields
        : []

      const addFirstMatchingKey = (candidateKeys) => {
        for (const key of candidateKeys) {
          if (!key || !discoveredKeySet.has(key) || orderedKeys.includes(key)) {
            continue
          }

          orderedKeys.push(key)
          return
        }
      }

      for (const field of formFields) {
        if (!field?.name) continue

        if (field.type === 'name') {
          addFirstMatchingKey([
            `__nameParts.${field.name}.first`,
            `${field.name}.first`,
          ])
          addFirstMatchingKey([
            `__nameParts.${field.name}.last`,
            `${field.name}.last`,
          ])
          continue
        }

        addFirstMatchingKey([field.name])
      }

      for (const field of teamManagerFields) {
        if (!field?.name) continue

        if (field.type === 'name') {
          addFirstMatchingKey([
            `teamManager.${field.name}.first`,
            `teamManager.__nameParts.${field.name}.first`,
            `teamManager_${field.name}.first`,
          ])
          addFirstMatchingKey([
            `teamManager.${field.name}.last`,
            `teamManager.__nameParts.${field.name}.last`,
            `teamManager_${field.name}.last`,
          ])
          continue
        }

        addFirstMatchingKey([`teamManager.${field.name}`, `teamManager_${field.name}`])
      }

      for (const key of discoveredKeys) {
        addFirstMatchingKey([key])
      }

      return orderedKeys
    }

    const availableFieldKeys = computed(() =>
      getOrderedDynamicFieldKeys(submissions.value).filter(
        (fieldKey) => !hiddenFieldKeys.value.has(fieldKey)
      )
    )

    const hiddenFieldKeys = computed(() => {
      const hiddenKeys = new Set()
      const formFields = Array.isArray(registrationForm.value?.fields)
        ? registrationForm.value.fields
        : []

      for (const field of formFields) {
        if (field?.type === 'name' && field.name) {
          hiddenKeys.add(field.name)
        }
      }

      return hiddenKeys
    })

    const formatFieldKeyLabel = (fieldKey) =>
      String(fieldKey || '')
        .split('.')
        .pop()
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (char) => char.toUpperCase())

    const fieldLabelMap = computed(() => {
      const labels = {}
      const formFields = Array.isArray(registrationForm.value?.fields)
        ? registrationForm.value.fields
        : []
      const teamManagerFields = Array.isArray(
        registrationForm.value?.settings?.teamManagerFields
      )
        ? registrationForm.value.settings.teamManagerFields
        : []

      for (const field of formFields) {
        if (!field?.name) continue
        labels[field.name] = field.label || formatFieldKeyLabel(field.name)

        if (field.type === 'name') {
          labels[`__nameParts.${field.name}.first`] =
            field.firstNameLabel || 'First Name'
          labels[`__nameParts.${field.name}.last`] =
            field.lastNameLabel || 'Last Name'
          labels[`${field.name}.first`] = field.firstNameLabel || 'First Name'
          labels[`${field.name}.last`] = field.lastNameLabel || 'Last Name'
        }
      }

      for (const field of teamManagerFields) {
        if (!field?.name) continue

        if (field.type === 'name') {
          labels[`teamManager.${field.name}.first`] =
            field.firstNameLabel || 'First Name'
          labels[`teamManager.${field.name}.last`] =
            field.lastNameLabel || 'Last Name'
          labels[`teamManager.__nameParts.${field.name}.first`] =
            field.firstNameLabel || 'First Name'
          labels[`teamManager.__nameParts.${field.name}.last`] =
            field.lastNameLabel || 'Last Name'
          labels[`teamManager_${field.name}.first`] =
            field.firstNameLabel || 'First Name'
          labels[`teamManager_${field.name}.last`] =
            field.lastNameLabel || 'Last Name'
          continue
        }

        labels[`teamManager.${field.name}`] =
          field.label || formatFieldKeyLabel(field.name)
        labels[`teamManager_${field.name}`] =
          field.label || formatFieldKeyLabel(field.name)
      }

      return labels
    })

    const defaultVisibleFieldKeys = computed(() =>
      availableFieldKeys.value.slice(0, 4)
    )

    watch(
      availableFieldKeys,
      (fieldKeys) => {
        const nextVisibleFieldKeys = visibleFieldKeys.value.filter((fieldKey) =>
          fieldKeys.includes(fieldKey)
        )

        if (nextVisibleFieldKeys.length > 0) {
          visibleFieldKeys.value = fieldKeys.filter((fieldKey) =>
            nextVisibleFieldKeys.includes(fieldKey)
          )
          return
        }

        visibleFieldKeys.value = defaultVisibleFieldKeys.value.slice()
      },
      { immediate: true }
    )

    const filteredSubmissions = computed(() => {
      const q = searchQuery.value.trim().toLowerCase()
      return (
        submissions.value
          .filter((s) =>
            sourceFilter.value === 'all'
              ? true
              : (s.source || 'race') === sourceFilter.value
          )
          .filter((s) => {
            if (!q) return true
            const flattenedValues = Object.values(
              getFlattenedSubmissionData(s)
            ).map((value) => toDisplayString(value))
            const hay = [
              s.id,
              s.source || '',
              s.submitted_at || '',
              ...flattenedValues,
            ]
              .join('\n')
              .toLowerCase()
            return hay.includes(q)
          })
          // Sort ascending by submission id
          .sort((a, b) => Number(a.id) - Number(b.id))
          .map((s) => ({ ...s }))
      )
    })

    const expandedSubmissions = computed(() =>
      submissions.value.filter((s) => expandedIds.value.has(s.id))
    )

    const toggleDetails = (s) => {
      const next = new Set(expandedIds.value)
      if (next.has(s.id)) {
        next.delete(s.id)
      } else {
        next.add(s.id)
      }
      expandedIds.value = next
    }

    const isExpanded = (id) => expandedIds.value.has(id)

    const isSelected = (id) => selectedIds.value.has(id)
    const selectedCount = computed(() => selectedIds.value.size)
    const allSelected = computed(() => {
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (idsInView.length === 0) return false
      for (const id of idsInView) {
        if (!selectedIds.value.has(id)) return false
      }
      return true
    })
    const toggleSelect = (id, evt) => {
      const next = new Set(selectedIds.value)
      if (evt && evt.target && evt.target.checked) {
        next.add(id)
      } else {
        next.delete(id)
      }
      selectedIds.value = next
    }
    const toggleSelectAll = (evt) => {
      const next = new Set(selectedIds.value)
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (evt && evt.target && evt.target.checked) {
        idsInView.forEach((id) => next.add(id))
      } else {
        idsInView.forEach((id) => next.delete(id))
      }
      selectedIds.value = next
    }
    const deleteSelected = async () => {
      if (!props.competition || !props.competition.id) return
      if (selectedIds.value.size === 0) return
      if (
        !confirm(
          t('competitions.confirmDeleteSubmission') ||
            'Delete selected submissions?'
        )
      )
        return
      const ids = Array.from(selectedIds.value)
      try {
        // delete sequentially to keep it simple and avoid rate limits
        for (const id of ids) {
          await axios.delete(
            `/registration/competition/${props.competition.id}/submission/${id}`
          )
          submissions.value = submissions.value.filter((x) => x.id !== id)
          expandedIds.value.delete(id)
          selectedIds.value.delete(id)
          if (editingSubmissionId.value === id) {
            resetEditState()
          }
        }
        emit('deleted-count', ids.length)
      } catch (e) {
        console.error('Error deleting selected submissions:', e)
        showNotification(
          t('competitions.failedToDeleteSubmission') ||
            'Failed to delete submission',
          'error'
        )
      }
    }

    const allExpanded = computed(() => {
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (idsInView.length === 0) return false
      for (const id of idsInView) {
        if (!expandedIds.value.has(id)) return false
      }
      return true
    })

    const toggleAllDetails = () => {
      const idsInView = filteredSubmissions.value.map((s) => s.id)
      if (idsInView.length === 0) return
      if (allExpanded.value) {
        const next = new Set(expandedIds.value)
        idsInView.forEach((id) => next.delete(id))
        expandedIds.value = next
      } else {
        const next = new Set(expandedIds.value)
        idsInView.forEach((id) => next.add(id))
        expandedIds.value = next
      }
    }

    const prettyJson = (obj) => {
      try {
        return JSON.stringify(
          obj && typeof obj === 'string' ? JSON.parse(obj) : obj,
          null,
          2
        )
      } catch {
        return JSON.stringify(obj, null, 2)
      }
    }

    const parseSubmissionData = (data) => {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch {
        return {}
      }
    }

    const flattenObject = (obj, prefix = '', result = {}) => {
      if (!obj || typeof obj !== 'object') return result
      for (const [key, value] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${key}` : key
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          flattenObject(value, fullKey, result)
        } else {
          result[fullKey] = Array.isArray(value) ? JSON.stringify(value) : value
        }
      }
      return result
    }

    const formatDateTime = (iso) => {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleString('et-EE')
    }

    const getColumnValue = (submission, fieldKey) => {
      const value = getFlattenedSubmissionData(submission)[fieldKey]
      return toDisplayString(value)
    }

    const getColumnLabel = (fieldKey) =>
      fieldLabelMap.value[fieldKey] || formatFieldKeyLabel(fieldKey)

    const getSubmissionSource = (submission) =>
      submission?.source || (submission?.cup_id ? 'cup' : 'race')

    const getSubmissionSourceLabel = (submission) =>
      getSubmissionSource(submission) === 'cup'
        ? translateOrFallback('cups.cups', 'Cup')
        : translateOrFallback('competitions.competition', 'Race')

    const isColumnVisible = (fieldKey) =>
      visibleFieldKeys.value.includes(fieldKey)

    const toggleColumnVisibility = (fieldKey, evt) => {
      const nextVisibleFieldKeys = new Set(visibleFieldKeys.value)

      if (evt && evt.target && evt.target.checked) {
        nextVisibleFieldKeys.add(fieldKey)
      } else {
        nextVisibleFieldKeys.delete(fieldKey)
      }

      visibleFieldKeys.value = availableFieldKeys.value.filter((availableKey) =>
        nextVisibleFieldKeys.has(availableKey)
      )
    }

    const deleteSubmission = async (s) => {
      if (!props.competition || !props.competition.id) return
      if (
        !confirm(
          t('competitions.confirmDeleteSubmission') || 'Delete this submission?'
        )
      )
        return
      try {
        await axios.delete(
          `/registration/competition/${props.competition.id}/submission/${s.id}`
        )
        submissions.value = submissions.value.filter((x) => x.id !== s.id)
        expandedIds.value.delete(s.id)
        if (editingSubmissionId.value === s.id) {
          resetEditState()
        }
        emit('deleted-count', 1)
      } catch (e) {
        console.error('Error deleting submission:', e)
        showNotification(
          t('competitions.failedToDeleteSubmission') ||
            'Failed to delete submission',
          'error'
        )
      }
    }

    const toDisplayString = (value) => {
      if (value === null || value === undefined) return ''
      if (typeof value === 'string') {
        const trimmedValue = value.trim()

        if (
          (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) ||
          (trimmedValue.startsWith('{') && trimmedValue.endsWith('}'))
        ) {
          try {
            return toDisplayString(JSON.parse(trimmedValue))
          } catch {
            // Keep the original string when it is not valid JSON.
          }
        }
      }
      if (Array.isArray(value)) {
        return value.map((item) => toDisplayString(item)).join(', ')
      }
      if (typeof value === 'object') {
        const entries = Object.entries(value)
        const selectedKeys = entries
          .filter(([, entryValue]) => entryValue === true)
          .map(([entryKey]) => formatFieldKeyLabel(entryKey))

        if (selectedKeys.length > 0) {
          return selectedKeys.join(', ')
        }

        const meaningfulValues = entries
          .map(([, entryValue]) => toDisplayString(entryValue))
          .filter(Boolean)

        if (meaningfulValues.length > 0) {
          return meaningfulValues.join(', ')
        }

        return ''
      }
      return String(value)
    }

    const editableFieldTypes = new Set([
      'text',
      'email',
      'number',
      'textarea',
      'name',
      'select',
      'radio',
      'checkbox',
      'date',
      'time',
    ])

    const editableFormFields = computed(() =>
      (Array.isArray(registrationForm.value?.fields)
        ? registrationForm.value.fields
        : []
      ).filter((field) => field?.name && editableFieldTypes.has(field.type))
    )

    const editableTeamManagerFields = computed(() =>
      (Array.isArray(registrationForm.value?.settings?.teamManagerFields)
        ? registrationForm.value.settings.teamManagerFields
        : []
      ).filter((field) => field?.name && editableFieldTypes.has(field.type))
    )

    const hasEditableFields = computed(
      () =>
        editableFormFields.value.length > 0 ||
        editableTeamManagerFields.value.length > 0
    )

    const getFieldOptions = (field) =>
      Array.isArray(field?.options)
        ? field.options
            .map((option) =>
              option && typeof option === 'object'
                ? option.value || option.label || ''
                : option
            )
            .filter(Boolean)
        : []

    const getEditInputType = (field) =>
      ['email', 'number', 'date', 'time'].includes(field?.type)
        ? field.type
        : 'text'

    const deepClone = (value) => {
      try {
        return JSON.parse(JSON.stringify(value))
      } catch {
        return value
      }
    }

    const getTruthyObjectKeys = (value) =>
      Object.entries(value || {})
        .filter(
          ([, entryValue]) =>
            entryValue === true ||
            entryValue === 'true' ||
            entryValue === 1 ||
            entryValue === '1'
        )
        .map(([entryKey]) => entryKey)

    const normalizeCheckboxDraftValue = (value) => {
      if (Array.isArray(value)) {
        return value.map((entry) => String(entry))
      }

      if (value && typeof value === 'object') {
        return getTruthyObjectKeys(value)
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim()
        if (
          (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) ||
          (trimmedValue.startsWith('{') && trimmedValue.endsWith('}'))
        ) {
          try {
            return normalizeCheckboxDraftValue(JSON.parse(trimmedValue))
          } catch {
            return trimmedValue ? [trimmedValue] : []
          }
        }

        return trimmedValue ? [trimmedValue] : []
      }

      return []
    }

    const createEmptyNameValue = () => ({ first: '', last: '' })
    const createEmptyDateValue = () => ({ day: '', month: '', year: '' })

    const getNameDraftValue = (submissionData, fieldName, scope = 'fields') => {
      const root =
        scope === 'teamManager'
          ? submissionData?.teamManager || submissionData
          : submissionData
      const prefixedKey =
        scope === 'teamManager' ? `teamManager_${fieldName}` : fieldName

      const candidates = [
        root?.__nameParts?.[fieldName],
        root?.[fieldName],
        submissionData?.__nameParts?.[fieldName],
        submissionData?.[prefixedKey],
      ]

      for (const candidate of candidates) {
        if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
          return {
            first: candidate.first ? String(candidate.first) : '',
            last: candidate.last ? String(candidate.last) : '',
          }
        }
      }

      const firstKey = `${fieldName}_first`
      const lastKey = `${fieldName}_last`
      if (
        root?.[firstKey] !== undefined ||
        root?.[lastKey] !== undefined ||
        submissionData?.[`teamManager_${firstKey}`] !== undefined ||
        submissionData?.[`teamManager_${lastKey}`] !== undefined
      ) {
        return {
          first: String(
            root?.[firstKey] ?? submissionData?.[`teamManager_${firstKey}`] ?? ''
          ),
          last: String(
            root?.[lastKey] ?? submissionData?.[`teamManager_${lastKey}`] ?? ''
          ),
        }
      }

      if (typeof root?.[fieldName] === 'string') {
        return { first: root[fieldName], last: '' }
      }

      if (typeof submissionData?.[prefixedKey] === 'string') {
        return { first: submissionData[prefixedKey], last: '' }
      }

      return createEmptyNameValue()
    }

    const getScalarDraftValue = (submissionData, field, scope = 'fields') => {
      if (scope === 'teamManager') {
        if (
          submissionData?.teamManager &&
          submissionData.teamManager[field.name] !== undefined
        ) {
          return deepClone(submissionData.teamManager[field.name])
        }

        if (submissionData?.[`teamManager_${field.name}`] !== undefined) {
          return deepClone(submissionData[`teamManager_${field.name}`])
        }
      } else if (submissionData?.[field.name] !== undefined) {
        return deepClone(submissionData[field.name])
      }

      if (field.type === 'checkbox') {
        return getFieldOptions(field).length > 0
          ? normalizeCheckboxDraftValue(undefined)
          : false
      }

      return ''
    }

    const getDateDraftValue = (submissionData, fieldName, scope = 'fields') => {
      const root =
        scope === 'teamManager'
          ? submissionData?.teamManager || submissionData
          : submissionData
      const prefixedKey =
        scope === 'teamManager' ? `teamManager_${fieldName}` : fieldName

      const candidates = [root?.[fieldName], submissionData?.[prefixedKey]]

      for (const candidate of candidates) {
        if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
          return {
            day: candidate.day ? String(candidate.day) : '',
            month: candidate.month ? String(candidate.month) : '',
            year: candidate.year ? String(candidate.year) : '',
          }
        }
      }

      if (typeof root?.[fieldName] === 'string') {
        const value = root[fieldName].trim()
        let match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
        if (match) {
          return { day: match[1], month: match[2], year: match[3] }
        }

        match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/)
        if (match) {
          return { day: match[1], month: match[2], year: match[3] }
        }

        match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        if (match) {
          return { year: match[1], month: match[2], day: match[3] }
        }
      }

      return createEmptyDateValue()
    }

    const createEditDraftFromSubmission = (submission) => {
      const submissionData = parseSubmissionData(submission.submission_data)
      const fields = {}
      const teamManager = {}

      editableFormFields.value.forEach((field) => {
        fields[field.name] =
          field.type === 'name'
            ? getNameDraftValue(submissionData, field.name, 'fields')
            : field.type === 'date'
              ? getDateDraftValue(submissionData, field.name, 'fields')
            : getScalarDraftValue(submissionData, field, 'fields')
      })

      editableTeamManagerFields.value.forEach((field) => {
        teamManager[field.name] =
          field.type === 'name'
            ? getNameDraftValue(submissionData, field.name, 'teamManager')
            : field.type === 'date'
              ? getDateDraftValue(submissionData, field.name, 'teamManager')
            : getScalarDraftValue(submissionData, field, 'teamManager')
      })

      return { fields, teamManager }
    }

    const isCheckboxDraftSelected = (draftValue, option) =>
      normalizeCheckboxDraftValue(draftValue).includes(String(option))

    const toggleCheckboxDraftValue = (scope, fieldName, option, event) => {
      const scopeDraft = editDraft.value[scope] || {}
      const nextValues = normalizeCheckboxDraftValue(scopeDraft[fieldName])
      const optionValue = String(option)

      if (event?.target?.checked) {
        if (!nextValues.includes(optionValue)) {
          nextValues.push(optionValue)
        }
      } else {
        const idx = nextValues.indexOf(optionValue)
        if (idx >= 0) {
          nextValues.splice(idx, 1)
        }
      }

      scopeDraft[fieldName] = nextValues
    }

    const setCheckboxPayloadValue = (target, field, draftValue, scope = 'fields') => {
      const options = getFieldOptions(field)

      if (options.length === 0) {
        if (scope === 'teamManager') {
          if (
            target?.teamManager &&
            Object.prototype.hasOwnProperty.call(target.teamManager, field.name)
          ) {
            target.teamManager[field.name] = !!draftValue
            return
          }

          if (
            Object.prototype.hasOwnProperty.call(target, `teamManager_${field.name}`)
          ) {
            target[`teamManager_${field.name}`] = !!draftValue
            return
          }

          if (!target.teamManager || typeof target.teamManager !== 'object') {
            target.teamManager = {}
          }
          target.teamManager[field.name] = !!draftValue
          return
        }

        target[field.name] = !!draftValue
        return
      }

      const selectedValues = normalizeCheckboxDraftValue(draftValue)
      const currentValue =
        scope === 'teamManager'
          ? target?.teamManager?.[field.name] ?? target?.[`teamManager_${field.name}`]
          : target?.[field.name]

      let nextValue
      if (Array.isArray(currentValue)) {
        nextValue = [...selectedValues]
      } else if (currentValue && typeof currentValue === 'object') {
        nextValue = { ...currentValue }
        options.forEach((option) => {
          nextValue[option] = selectedValues.includes(String(option))
        })
      } else if (typeof currentValue === 'string') {
        const trimmedValue = currentValue.trim()
        if (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) {
          try {
            const parsed = JSON.parse(trimmedValue)
            nextValue = { ...parsed }
            options.forEach((option) => {
              nextValue[option] = selectedValues.includes(String(option))
            })
          } catch {
            nextValue = [...selectedValues]
          }
        } else {
          nextValue = [...selectedValues]
        }
      } else {
        nextValue = [...selectedValues]
      }

      if (scope === 'teamManager') {
        if (
          target?.teamManager &&
          Object.prototype.hasOwnProperty.call(target.teamManager, field.name)
        ) {
          target.teamManager[field.name] = nextValue
          return
        }

        if (
          Object.prototype.hasOwnProperty.call(target, `teamManager_${field.name}`)
        ) {
          target[`teamManager_${field.name}`] = nextValue
          return
        }

        if (!target.teamManager || typeof target.teamManager !== 'object') {
          target.teamManager = {}
        }
        target.teamManager[field.name] = nextValue
        return
      }

      target[field.name] = nextValue
    }

    const setNamePayloadValue = (target, fieldName, nameValue, scope = 'fields') => {
      const normalizedValue = {
        first: String(nameValue?.first ?? ''),
        last: String(nameValue?.last ?? ''),
      }
      const displayValue = `${normalizedValue.first} ${normalizedValue.last}`.trim()

      if (scope === 'teamManager') {
        // Match registration.js.template + registrationService: name fields are
        // teamManager_<field>_first / teamManager_<field>_last at submit time,
        // stored per-athlete as teamManager: { "<field>_first", "<field>_last" }.
        const tmHasNameShape =
          target?.teamManager &&
          typeof target.teamManager === 'object' &&
          (Object.prototype.hasOwnProperty.call(target.teamManager, fieldName) ||
            Object.prototype.hasOwnProperty.call(
              target.teamManager,
              `${fieldName}_first`
            ) ||
            Object.prototype.hasOwnProperty.call(
              target.teamManager,
              `${fieldName}_last`
            ))

        const applyNestedSplit = () => {
          if (!target.teamManager || typeof target.teamManager !== 'object') {
            target.teamManager = {}
          }
          const tm = target.teamManager
          tm[`${fieldName}_first`] = normalizedValue.first
          tm[`${fieldName}_last`] = normalizedValue.last
        }

        const applyFlatSplit = () => {
          target[`teamManager_${fieldName}_first`] = normalizedValue.first
          target[`teamManager_${fieldName}_last`] = normalizedValue.last
        }

        if (tmHasNameShape) {
          applyNestedSplit()
          return
        }

        if (
          Object.prototype.hasOwnProperty.call(target, `teamManager_${fieldName}`) ||
          Object.prototype.hasOwnProperty.call(
            target,
            `teamManager_${fieldName}_first`
          ) ||
          Object.prototype.hasOwnProperty.call(target, `teamManager_${fieldName}_last`)
        ) {
          applyFlatSplit()
          return
        }

        if (
          target.teamManager &&
          typeof target.teamManager === 'object' &&
          Object.keys(target.teamManager).length > 0
        ) {
          applyNestedSplit()
          return
        }

        applyFlatSplit()
        return
      }

      if (!target.__nameParts || typeof target.__nameParts !== 'object') {
        target.__nameParts = {}
      }

      target.__nameParts[fieldName] = normalizedValue
      target[fieldName] = displayValue
    }

    const setDatePayloadValue = (target, fieldName, dateValue, scope = 'fields') => {
      const day = String(dateValue?.day ?? '').trim()
      const month = String(dateValue?.month ?? '').trim()
      const year = String(dateValue?.year ?? '').trim()
      const normalizedValue =
        day && month && year
          ? `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`
          : ''

      if (scope === 'teamManager') {
        if (
          target?.teamManager &&
          Object.prototype.hasOwnProperty.call(target.teamManager, fieldName)
        ) {
          target.teamManager[fieldName] = normalizedValue
          return
        }

        if (
          Object.prototype.hasOwnProperty.call(target, `teamManager_${fieldName}`)
        ) {
          target[`teamManager_${fieldName}`] = normalizedValue
          return
        }

        if (!target.teamManager || typeof target.teamManager !== 'object') {
          target.teamManager = {}
        }
        target.teamManager[fieldName] = normalizedValue
        return
      }

      target[fieldName] = normalizedValue
    }

    const setScalarPayloadValue = (target, field, value, scope = 'fields') => {
      if (scope === 'teamManager') {
        if (
          target?.teamManager &&
          Object.prototype.hasOwnProperty.call(target.teamManager, field.name)
        ) {
          target.teamManager[field.name] = value
          return
        }

        if (
          Object.prototype.hasOwnProperty.call(target, `teamManager_${field.name}`)
        ) {
          target[`teamManager_${field.name}`] = value
          return
        }

        if (!target.teamManager || typeof target.teamManager !== 'object') {
          target.teamManager = {}
        }
        target.teamManager[field.name] = value
        return
      }

      target[field.name] = value
    }

    const buildSubmissionPayloadFromDraft = (draft, submission) => {
      const originalData = parseSubmissionData(submission?.submission_data)
      const nextData = deepClone(originalData) || {}

      editableFormFields.value.forEach((field) => {
        const draftValue = draft?.fields?.[field.name]
        if (field.type === 'name') {
          setNamePayloadValue(nextData, field.name, draftValue, 'fields')
          return
        }

        if (field.type === 'date') {
          setDatePayloadValue(nextData, field.name, draftValue, 'fields')
          return
        }

        if (field.type === 'checkbox') {
          setCheckboxPayloadValue(nextData, field, draftValue, 'fields')
          return
        }

        setScalarPayloadValue(nextData, field, draftValue, 'fields')
      })

      editableTeamManagerFields.value.forEach((field) => {
        const draftValue = draft?.teamManager?.[field.name]
        if (field.type === 'name') {
          setNamePayloadValue(nextData, field.name, draftValue, 'teamManager')
          return
        }

        if (field.type === 'date') {
          setDatePayloadValue(nextData, field.name, draftValue, 'teamManager')
          return
        }

        if (field.type === 'checkbox') {
          setCheckboxPayloadValue(nextData, field, draftValue, 'teamManager')
          return
        }

        setScalarPayloadValue(nextData, field, draftValue, 'teamManager')
      })

      return nextData
    }

    const isEditModalVisible = computed(() => !!editingSubmissionId.value)

    const startEditing = (submission) => {
      editingSubmissionId.value = submission.id
      editingSubmission.value = submission
      editDraft.value = createEditDraftFromSubmission(submission)
    }

    const cancelEditing = () => {
      resetEditState()
    }

    const saveSubmissionEdit = async (submission = editingSubmission.value) => {
      if (!props.competition?.id || savingEdit.value) return

      savingEdit.value = true

      try {
        const submission_data = buildSubmissionPayloadFromDraft(
          editDraft.value,
          submission
        )
        const res = await axios.put(
          `/registration/competition/${props.competition.id}/submission/${submission.id}`,
          { submission_data }
        )

        const updatedSubmission = res?.data
          ? { ...submission, ...res.data }
          : { ...submission, submission_data }

        submissions.value = submissions.value.map((item) =>
          item.id === submission.id ? updatedSubmission : item
        )

        resetEditState()
        showNotification(
          t('competitions.submissionUpdatedSuccess') ||
            'Submission updated successfully',
          'success'
        )
      } catch (error) {
        console.error('Error updating submission:', error)
        showNotification(
          t('competitions.submissionUpdatedError') ||
            'Failed to update submission',
          'error'
        )
      } finally {
        savingEdit.value = false
      }
    }

    // Replace raw database IDs with sequential indices based on ascending order
    const formatIndex = (id) => {
      const ordered = [...filteredSubmissions.value]
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map((s) => s.id)
      const idx = ordered.indexOf(id)
      return idx >= 0 ? idx + 1 : id
    }

    const getAthleteFieldEntries = (s) => {
      // Parse submission_data, prefer athlete fields if present
      const data = parseSubmissionData(s.submission_data)
      // If data contains top-level athlete-like fields, use those; otherwise, use flattened data
      const flattened = flattenObject(data)

      // Prefer a curated subset of athlete-related keys (common patterns)
      const preferredKeys = [
        'name',
        'fullName',
        'firstName',
        'lastName',
        'email',
        'phone',
        'gender',
        'birthDate',
        'nationality',
        'club',
        'team',
        'category',
        'distance',
      ]

      const entries = []

      // Add preferred keys that exist
      for (const key of preferredKeys) {
        if (flattened[key] !== undefined) {
          entries.push([key, toDisplayString(flattened[key])])
        }
      }

      // Add remaining keys, excluding arrays of athletes or teamManager objects already flattened
      const added = new Set(entries.map((e) => e[0]))
      for (const [k, v] of Object.entries(flattened)) {
        if (added.has(k)) continue
        // Skip internal/grouping keys
        if (k === 'athletes') continue
        entries.push([k, toDisplayString(v)])
      }

      return entries
    }

    const quoteCsv = (val) => {
      const v = String(val)
      if (v.includes(';') || v.includes('"') || v.includes('\n')) {
        return '"' + v.replace(/\"/g, '""') + '"'
      }
      return v
    }

    const findCheckboxFieldMetaByDynamicKey = (dynamicKey) => {
      const formFields = Array.isArray(registrationForm.value?.fields)
        ? registrationForm.value.fields
        : []
      for (const field of formFields) {
        if (!field?.name || field.type !== 'checkbox') continue
        if (getFieldOptions(field).length === 0) continue
        const name = field.name
        if (dynamicKey === name || dynamicKey.startsWith(`${name}.`)) {
          return {
            field,
            scope: 'fields',
            storagePrefix: name,
            storagePrefixAlt: null,
          }
        }
      }

      const teamManagerFields = Array.isArray(
        registrationForm.value?.settings?.teamManagerFields
      )
        ? registrationForm.value.settings.teamManagerFields
        : []
      for (const field of teamManagerFields) {
        if (!field?.name || field.type !== 'checkbox') continue
        if (getFieldOptions(field).length === 0) continue
        const tmDot = `teamManager.${field.name}`
        const tmUnder = `teamManager_${field.name}`
        if (dynamicKey === tmUnder || dynamicKey.startsWith(`${tmUnder}.`)) {
          return {
            field,
            scope: 'teamManager',
            storagePrefix: tmUnder,
            storagePrefixAlt: tmDot,
          }
        }
        if (dynamicKey === tmDot || dynamicKey.startsWith(`${tmDot}.`)) {
          return {
            field,
            scope: 'teamManager',
            storagePrefix: tmDot,
            storagePrefixAlt: tmUnder,
          }
        }
      }

      return null
    }

    const shouldSkipDynamicKeyAsCheckboxSubkey = (h, processedMetas) => {
      for (const { storagePrefix, storagePrefixAlt, field } of processedMetas) {
        const prefixes = [storagePrefix, storagePrefixAlt].filter(
          (p, idx, arr) => p && arr.indexOf(p) === idx
        )
        const optStrings = getFieldOptions(field).map((o) => String(o))
        for (const prefix of prefixes) {
          if (h === prefix) return true
          if (!h.startsWith(`${prefix}.`)) continue
          const rest = h.slice(prefix.length + 1)
          if (rest.includes('.')) continue
          if (optStrings.includes(rest)) return true
        }
      }
      return false
    }

    const buildExportColumnDescriptors = (orderedDynamicKeys) => {
      const processedIds = new Set()
      const processedMetas = []
      const descriptors = []

      for (const h of orderedDynamicKeys) {
        const meta = findCheckboxFieldMetaByDynamicKey(h)
        if (meta) {
          const id = `${meta.scope}:${meta.field.name}`
          if (processedIds.has(id)) {
            continue
          }
          processedIds.add(id)
          processedMetas.push({
            storagePrefix: meta.storagePrefix,
            storagePrefixAlt: meta.storagePrefixAlt,
            field: meta.field,
          })
          for (const opt of getFieldOptions(meta.field)) {
            descriptors.push({
              type: 'checkboxOption',
              field: meta.field,
              scope: meta.scope,
              storagePrefix: meta.storagePrefix,
              storagePrefixAlt: meta.storagePrefixAlt,
              option: String(opt),
            })
          }
          continue
        }

        if (shouldSkipDynamicKeyAsCheckboxSubkey(h, processedMetas)) {
          continue
        }

        descriptors.push({ type: 'simple', key: h })
      }

      return descriptors
    }

    const getCheckboxOptionExportLabel = (field, optionValue) => {
      const opts = Array.isArray(field?.options) ? field.options : []
      const needle = String(optionValue)
      for (const o of opts) {
        if (o && typeof o === 'object') {
          const v =
            o.value != null && o.value !== '' ? String(o.value) : ''
          const l =
            o.label != null && o.label !== '' ? String(o.label) : ''
          if (v === needle) return l || v
          if (l === needle) return l || v
        } else if (String(o) === needle) {
          return String(o)
        }
      }
      return needle
    }

    const isTruthyCheckboxFlatValue = (v) =>
      v === true || v === 'true' || v === 1 || v === '1'

    const getExportDynamicCellValue = (submission, descriptor) => {
      if (descriptor.type === 'simple') {
        return getColumnValue(submission, descriptor.key)
      }

      const { storagePrefix, storagePrefixAlt, option, field } = descriptor
      const flat = getFlattenedSubmissionData(submission)
      const prefixes = [storagePrefix, storagePrefixAlt].filter(
        (p, idx, arr) => p && arr.indexOf(p) === idx
      )
      const optionStr = String(option)
      const label = getCheckboxOptionExportLabel(field, optionStr)

      for (const prefix of prefixes) {
        const direct = flat[`${prefix}.${optionStr}`]
        if (isTruthyCheckboxFlatValue(direct)) {
          return label
        }
      }

      for (const prefix of prefixes) {
        const parent = flat[prefix]
        if (parent !== undefined) {
          const normalized = normalizeCheckboxDraftValue(parent)
          if (normalized.includes(optionStr)) {
            return label
          }
        }
      }

      return ''
    }

    const getTeamManagerNamePartKeys = (fieldName) => [
      `teamManager.__nameParts.${fieldName}.first`,
      `teamManager.__nameParts.${fieldName}.last`,
      `teamManager.${fieldName}.first`,
      `teamManager.${fieldName}.last`,
      `teamManager_${fieldName}.first`,
      `teamManager_${fieldName}.last`,
    ]

    const getTeamManagerNameCompositeKey = (fieldName, submissionList) => {
      const discoveredSet = new Set(collectFieldKeysFromSubmissions(submissionList))
      const dot = `teamManager.${fieldName}`
      const under = `teamManager_${fieldName}`
      if (discoveredSet.has(dot)) return dot
      if (discoveredSet.has(under)) return under
      return dot
    }

    /** CSV only: one column teamManager.<fieldName>, not __nameParts .first/.last. */
    const sanitizeDynamicKeysForCsvExport = (orderedKeys, submissionList) => {
      const teamManagerFields = Array.isArray(
        registrationForm.value?.settings?.teamManagerFields
      )
        ? registrationForm.value.settings.teamManagerFields
        : []

      const nameFieldNames = teamManagerFields
        .filter((f) => f?.type === 'name' && f.name)
        .map((f) => f.name)

      if (nameFieldNames.length === 0) return orderedKeys

      const partKeySets = new Map(
        nameFieldNames.map((fn) => [fn, new Set(getTeamManagerNamePartKeys(fn))])
      )

      const isTeamManagerNameKey = (key, fieldName) => {
        const composite = getTeamManagerNameCompositeKey(fieldName, submissionList)
        if (key === composite) return true
        return partKeySets.get(fieldName)?.has(key) ?? false
      }

      const emittedComposite = new Set()
      const result = []

      for (const key of orderedKeys) {
        let consumed = false
        for (const fieldName of nameFieldNames) {
          if (!isTeamManagerNameKey(key, fieldName)) continue
          if (emittedComposite.has(fieldName)) {
            consumed = true
            break
          }
          result.push(getTeamManagerNameCompositeKey(fieldName, submissionList))
          emittedComposite.add(fieldName)
          consumed = true
          break
        }
        if (consumed) continue
        result.push(key)
      }

      return result
    }

    const exportCsv = () => {
      // Base columns available from the submission record
      const baseHeaders = [
        'id',
        'source',
        'submitted_at',
      ]

      const dynamicHeaders = sanitizeDynamicKeysForCsvExport(
        getOrderedDynamicFieldKeys(filteredSubmissions.value),
        filteredSubmissions.value
      )

      const dynamicDescriptors = buildExportColumnDescriptors(dynamicHeaders)

      const baseHeaderLabels = baseHeaders.map((header) => {
        switch (header) {
          case 'id':
            return 'ID'
          case 'source':
            return translateOrFallback('general.source', 'Source')
          case 'submitted_at':
            return translateOrFallback(
              'competitions.submittedAt',
              'Submitted At'
            )
          default:
            return header
        }
      })

      const dynamicHeaderLabels = dynamicDescriptors.map((d) => {
        if (d.type === 'simple') {
          return getColumnLabel(d.key)
        }
        const baseLabel = getColumnLabel(d.storagePrefix)
        const optLabel = getCheckboxOptionExportLabel(d.field, d.option)
        return `${baseLabel} - ${optLabel}`
      })

      const headerLabels = [...baseHeaderLabels, ...dynamicHeaderLabels]

      const cellValue = (s, key) => {
        switch (key) {
          case 'id':
            return s.id
          case 'source':
            return s.source || (s.cup_id ? 'cup' : 'race')
          case 'submitted_at':
            return s.submitted_at == null ? '' : s.submitted_at
          default: {
            return getColumnValue(s, key)
          }
        }
      }

      const rows = filteredSubmissions.value.map((s) => {
        const baseCells = baseHeaders.map((h) => quoteCsv(cellValue(s, h)))
        const dynamicCells = dynamicDescriptors.map((d) =>
          quoteCsv(getExportDynamicCellValue(s, d))
        )
        return [...baseCells, ...dynamicCells]
      })

      const csv = [headerLabels.map(quoteCsv).join(';'), ...rows.map((r) => r.join(';'))].join(
        '\n'
      )
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `submissions_${
        (props.competition && props.competition.id) || 'export'
      }.csv`
      link.click()
      URL.revokeObjectURL(url)
    }

    const onClose = () => {
      resetEditState()
      emit('close')
    }
    const visibleColumnSpan = computed(() => visibleFieldKeys.value.length + 4)

    return {
      t,
      showNotificationFlag,
      notificationMessage,
      notificationType,
      loading,
      searchQuery,
      sourceFilter,
      sourceHeaderLabel,
      submittedAtHeaderLabel,
      columnsLabel,
      noSubmissionFieldsText,
      noSubmissionsFoundText,
      registrationForm,
      columnSelectorRef,
      submissions,
      filteredSubmissions,
      expandedSubmissions,
      toggleDetails,
      isExpanded,
      isSelected,
      selectedCount,
      allSelected,
      toggleSelect,
      toggleSelectAll,
      deleteSelected,
      allExpanded,
      toggleAllDetails,
      prettyJson,
      formatDateTime,
      formatIndex,
      deleteSubmission,
      getAthleteFieldEntries,
      availableFieldKeys,
      visibleFieldKeys,
      visibleColumnSpan,
      getColumnLabel,
      getColumnValue,
      getSubmissionSource,
      getSubmissionSourceLabel,
      isColumnVisible,
      toggleColumnVisibility,
      editingSubmission,
      editableFormFields,
      editableTeamManagerFields,
      hasEditableFields,
      editDraft,
      savingEdit,
      isEditModalVisible,
      startEditing,
      cancelEditing,
      saveSubmissionEdit,
      getFieldOptions,
      getEditInputType,
      isCheckboxDraftSelected,
      toggleCheckboxDraftValue,
      exportCsv,
      onClose,
    }
  },
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal-content.submissions-modal {
  background: white;
  border-radius: 12px;
  width: min(98vw, 1400px);
  max-width: 1400px;
  min-height: 70vh;
  max-height: 94vh;
  overflow: visible;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}
.edit-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  z-index: 1100;
}
.modal-content.submission-edit-modal {
  width: min(900px, 92vw);
  max-height: 90vh;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}
.modal-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 0.75rem 1rem;
  overflow: visible;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #dee2e6;
}
.notification {
  margin: 0.75rem 1rem 0;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
}
.notification.success {
  background: #e9f7ef;
  color: #1f5134;
  border: 1px solid #cfe8d7;
}
.notification.error {
  background: #fbebee;
  color: #7a1f2b;
  border: 1px solid #f2c8cf;
}
.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}
.toolbar {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 5;
}
.search-input {
  flex: 1 1 240px;
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.filter-select {
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
}
.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}
.column-selector {
  position: relative;
}
.column-selector-summary {
  list-style: none;
  cursor: pointer;
  padding: 0.45rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background: #fff;
  color: #212529;
  font-size: 0.85rem;
  white-space: nowrap;
}
.column-selector-summary::-webkit-details-marker {
  display: none;
}
.column-selector-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 0.35rem);
  z-index: 20;
  min-width: 260px;
  max-width: 360px;
  max-height: min(60vh, 520px);
  overflow: auto;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.column-option {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  padding: 0.35rem 0;
  font-size: 0.85rem;
  color: #212529;
}
.column-option-label {
  word-break: break-word;
}
.column-empty {
  padding: 0.25rem 0;
  color: #6c757d;
  font-size: 0.85rem;
}
.submissions-table {
  width: max-content;
  table-layout: auto;
  border-collapse: collapse;
  min-width: 100%;
}
.submissions-table th {
  background: #f8f9fa;
  text-align: left;
  padding: 0.5rem;
  font-weight: 600;
  font-size: 0.85rem;
  border-bottom: 1px solid #dee2e6;
  white-space: nowrap;
}
.submissions-table th:last-child {
  text-align: right;
}
.submissions-table td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: top;
  white-space: nowrap;
}
.dynamic-column-header,
.dynamic-cell {
  min-width: max-content;
  max-width: none;
}
.source-header,
.source-cell {
  width: auto;
}
.dynamic-cell-value {
  display: block;
  overflow: visible;
  text-overflow: clip;
  white-space: inherit;
}
.submissions-table td:last-child {
  text-align: right;
}
.submissions-table td:last-child .actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.submissions-table .mono {
  font-family: ui-monospace, Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 0.85rem;
}
.date-cell {
  white-space: nowrap;
  color: #6c757d;
  font-size: 0.85rem;
  text-align: right;
}
.date-cell-header {
  text-align: right !important;
}
.empty-state {
  padding: 1rem !important;
  text-align: center !important;
  color: #6c757d;
}
.source-pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.source-pill.race {
  background: #e2e3e5;
  color: #495057;
}
.source-pill.cup {
  background: #cce5ff;
  color: #004085;
}
.details-cell {
  background: #f8f9fa;
}
.details {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #fff;
}
.submission-edit-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #dee2e6;
}
.submission-edit-title-group h4 {
  margin: 0;
  font-size: 1rem;
  color: #212529;
}
.submission-edit-subtitle {
  margin: 0.25rem 0 0;
  color: #6c757d;
  font-size: 0.85rem;
}
.submission-edit-body {
  flex: 1;
  overflow: auto;
  padding: 1rem 1.25rem;
}
.submission-edit-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #dee2e6;
  background: #fff;
}
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.edit-section {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.875rem;
  background: #ffffff;
}
.edit-section-title {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  color: #212529;
}
.edit-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.875rem;
}
.edit-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.edit-group-span {
  grid-column: 1 / -1;
}
.edit-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #343a40;
}
.edit-sublabel {
  font-size: 0.8rem;
  color: #495057;
}
.edit-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.edit-input,
.edit-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 0.55rem 0.65rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #212529;
  background: #fff;
}
.edit-input:focus,
.edit-textarea:focus {
  outline: none;
  border-color: #868e96;
  box-shadow: 0 0 0 2px rgba(73, 80, 87, 0.12);
}
.edit-textarea {
  resize: vertical;
}
.edit-name-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}
.edit-date-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}
.edit-checkbox-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem 0.75rem;
}
.edit-checkbox-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #343a40;
}
.edit-checkbox-single {
  margin-top: 0.15rem;
}
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.edit-empty {
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  color: #6c757d;
  background: #fff;
}
.kv-table {
  width: 100%;
  border-collapse: collapse;
}
.kv-table thead th {
  text-align: left;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.4rem 0.5rem;
  font-size: 0.8rem;
}
.kv-table td {
  border-bottom: 1px solid #f1f3f5;
  padding: 0.4rem 0.5rem;
  vertical-align: top;
}
.kv-key {
  width: 30%;
  font-weight: 600;
  color: #495057;
}
.kv-value {
  width: 70%;
  color: #212529;
  word-break: break-word;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}
.btn-secondary {
  background: #6c757d;
  color: #fff;
}
.btn-primary {
  background: #343a40;
  color: #fff;
}
.btn-danger {
  background: #dc3545;
  color: #fff;
}
.btn-sm {
  padding: 0.2rem 0.4rem;
  font-size: 0.7rem;
}
.loading {
  padding: 1rem;
  color: #6c757d;
}
@media (max-width: 900px) {
  .edit-grid,
  .edit-name-grid,
  .edit-date-grid,
  .edit-checkbox-grid {
    grid-template-columns: 1fr;
  }

  .edit-modal-overlay {
    padding: 0.75rem;
  }
}
</style>
